import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, Pause, HelpCircle, StopCircle, RotateCcw, Activity, Users, SkipForward } from 'lucide-react';
import axios from 'axios';
import { useWebSocket } from '../hooks/useWebSocket';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GameOperator = () => {
  const [gameState, setGameState] = useState({
    state: 'lobby',
    users_count: 0,
    active_players: 0,
    eliminated_players: 0,
    current_question_index: -1
  });
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { lastMessage, isConnected } = useWebSocket();

  useEffect(() => {
    fetchState();
    fetchQuizData();
  }, []);

  useEffect(() => {
    if (!lastMessage) return;
    
    if (lastMessage.type === 'game_state') {
      setGameState(prev => ({
        ...prev,
        state: lastMessage.state,
        users_count: lastMessage.users_count ?? prev.users_count
      }));
    } else if (lastMessage.type === 'results') {
      setGameState(prev => ({
        ...prev,
        active_players: lastMessage.survivors_count,
        eliminated_players: lastMessage.eliminated_count
      }));
    } else if (lastMessage.type === 'init' && lastMessage.game) {
      setGameState(lastMessage.game);
    }
  }, [lastMessage]);

  const fetchState = async () => {
    try {
      const res = await axios.get(`${API}/game/state`);
      setGameState(res.data);
    } catch (e) {
      console.error('Failed to fetch game state:', e);
    }
  };

  const fetchQuizData = async () => {
    try {
      const res = await axios.get(`${API}/game/quiz`);
      setQuizData(res.data);
    } catch (e) {
      console.error('Failed to fetch quiz data:', e);
    }
  };

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await axios.post(`${API}/game/${action}`);
      await fetchState();
    } catch (e) {
      console.error(`Action ${action} failed:`, e);
    }
    setLoading(false);
  };

  const currentQ = quizData?.questions?.[gameState.current_question_index];
  const nextQ = quizData?.questions?.[gameState.current_question_index + 1];

  return (
    <div className="operator-layout" data-testid="game-operator">
      <div className="wave-background opacity-30" />
      
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="operator-header">
          <div className="flex items-center gap-4">
            <Link to="/" className="nav-link" data-testid="back-link">
              <ArrowLeft size={20} />
              Back
            </Link>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">
              Game Operator
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className={`status-badge ${isConnected ? 'active' : 'eliminated'}`}>
              <Activity size={14} />
              {isConnected ? 'Connected' : 'Disconnected'}
            </div>
            <Link 
              to="/game/screen" 
              target="_blank"
              className="operator-btn secondary"
              data-testid="view-screen-link"
            >
              View Screen
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="stat-card" data-testid="stat-state">
            <div className="stat-value text-2xl uppercase">{gameState.state}</div>
            <div className="stat-label">Current State</div>
          </div>
          <div className="stat-card" data-testid="stat-total-users">
            <div className="stat-value">{gameState.users_count}</div>
            <div className="stat-label">Total Players</div>
          </div>
          <div className="stat-card" data-testid="stat-active">
            <div className="stat-value text-green-400">{gameState.active_players}</div>
            <div className="stat-label">Active</div>
          </div>
          <div className="stat-card" data-testid="stat-eliminated">
            <div className="stat-value text-red-400">{gameState.eliminated_players}</div>
            <div className="stat-label">Eliminated</div>
          </div>
        </div>

        {/* Current Question Info */}
        {currentQ && (
          <div className="operator-card p-6 mb-6">
            <div className="text-sm text-slate-400 mb-2">Current Question</div>
            <div className="text-xl text-white font-semibold mb-2">{currentQ.question}</div>
            <div className="text-sm text-green-400">Correct: {currentQ.correct}</div>
          </div>
        )}

        {/* Next Question Preview */}
        {nextQ && (
          <div className="operator-card p-6 mb-6 border-dashed">
            <div className="text-sm text-slate-400 mb-2">Next Question</div>
            <div className="text-lg text-slate-300">{nextQ.question}</div>
          </div>
        )}

        {/* Controls */}
        <div className="operator-card p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Controls</h2>
          
          <div className="operator-grid">
            <button
              onClick={() => handleAction('open-lobby')}
              className="operator-btn secondary"
              disabled={loading}
              data-testid="open-lobby-btn"
            >
              <Users size={18} className="inline mr-2" />
              Open Lobby
            </button>

            <button
              onClick={() => handleAction('start')}
              className="operator-btn"
              disabled={loading || gameState.state === 'playing'}
              data-testid="start-game-btn"
            >
              <Play size={18} className="inline mr-2" />
              Start Game
            </button>

            <button
              onClick={() => handleAction('pause')}
              className="operator-btn secondary"
              disabled={loading || gameState.state !== 'playing'}
              data-testid="pause-btn"
            >
              <Pause size={18} className="inline mr-2" />
              Force Pause
            </button>

            <button
              onClick={() => handleAction('show-question')}
              className="operator-btn"
              disabled={loading}
              data-testid="show-question-btn"
            >
              <HelpCircle size={18} className="inline mr-2" />
              Show Question
            </button>

            <button
              onClick={() => handleAction('end-question')}
              className="operator-btn secondary"
              disabled={loading || gameState.state !== 'question'}
              data-testid="end-question-btn"
            >
              <StopCircle size={18} className="inline mr-2" />
              End Question
            </button>

            <button
              onClick={() => handleAction('resume')}
              className="operator-btn secondary"
              disabled={loading || gameState.state === 'playing'}
              data-testid="resume-btn"
            >
              <SkipForward size={18} className="inline mr-2" />
              Resume Video
            </button>

            <button
              onClick={() => handleAction('reset')}
              className="operator-btn danger"
              disabled={loading}
              data-testid="reset-game-btn"
            >
              <RotateCcw size={18} className="inline mr-2" />
              Reset Game
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameOperator;
